import React from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { Typography } from '@wds/typography';
import { Box } from '@wds/box';

const uisConfigure = `{
	"mapping": {
		"user.first_name": "First name column",
		"user.last_name": "Last name column",
		"user.email": "Email column",
		"user.timezone": "tz column",
		"custom_fields": {
			"Test Applicant CF": 7
		}
	}
}`;

const uisSampleImport = `First name column,Last name column,Email column,Test Applicant CF,tz
Jane,Smith,jane.smith@mailinator.com,Pacific/Norfolk`;

const requiredAttributesExample = `First name column,Last name column,Email column
Jane,Smith,jane.smith@mailinator.com`;

const requiredUniqueAttributesExample = `First name column,Last name column,Email column,STUDENT_ID
Jane,Smith,jane.smith@mailinator.com,123456`;

const requiredUniqueOptionalAttributesExample = `First name column,Last name column,Email column,STUDENT_ID,GPA,timezone
Jane,Smith,jane.smith@mailinator.com,123456,4.0,America/Toronto`;

const uisMappingExample = `"mapping": {
"user.first_name": "GivenName",
...
}`;

const warningExample = `{
"warnings": [
   {
	   "unmapped_columns": [
		   "unmapped column 'Test Applicant Field 2' found"
	   ]
   },
   {
	   "missing_optional_columns": [
		   "column 'tz' is missing from import csv for attribute 'user.timezone'. Site timezone will be used instead."
	   ]
   }
]
}`;

const errorExample = `{
"errors": [
   {
	   "missing_required_columns": [
		   "missing required mapped column 'UserFirstNameAttribute' for attribute 'user.first_name'"
	   ]
   }
]
}`;

const sampleJobResponse = `{
   "id": 7,
   "created_at": "2022-12-31T17:27:07.390",
   "started_by": {
	   "id": 1,
	   "first_name": "Alexi",
	   "last_name": "Belsky",
	   "email": "alexibelsky@nbn.fake"
   },
   "status": 0,
   "job_id": "",
   "total_rows": 0,
   "processed": 0,
   "warnings": [],
   "errors": []
}`;

function UserInformationSystem(props) {
	const { theme } = props;

	return (
		<Box>
			<Typography
				variant='sectionTitle'
				component='h1'
				weight='medium'
			>
				Authentication
			</Typography>
			<Typography component='p'>
				All User Information System API call require API
				authentication; see
				<a
					href='#authentication?route=auth'
				>
					Authentication
				</a>
			</Typography>
			<Box my={5}>
				<Typography variant='cardTitle' component='h4' weight='medium'>
					Configuring User
					Information System
				</Typography>
				<Typography component='p'>
					The User Information System can be configured through the
					user interface or through the API.
				</Typography>

				<Typography component='p'>
					The User Information System integration page, available in
					{' '}
					<b>Settings</b>
					{' '}
					-&gt;
					{' '}
					<b>
						Integrations
					</b>
					{' '}
					-&gt;
					{' '}
					<b>User Information System</b>
					{' '}
					-&gt;
					{' '}
					<b>Configure</b>
					{' '}
					will be
					updated to
					support the new requirements. An example of the new view is below. If you do not
					have access to
					the User Information System card on the Integrations page, please contact
					support for more
					information on how to get access to this feature.
				</Typography>
				<Box my={2}>
					<Typography
						variant='cardTitle'
						component='h4'
						weight='medium'
					>
						Example:
					</Typography>
				</Box>
				<Typography component='p'>
					To configure UIS through the API, make a POST call to the
					{' '}
					<a
						href='#?route=patch-/api/uis/'
					>
						/api/uis/
					</a>
					endpoint with the desired mapping. Here’s an example of valid payload to
					configure UIS:
				</Typography>
				<Box my={5} ml={5} mr={10} pr={10}>
					<SyntaxHighlighter
						language='json'
						style={theme}
					>
						{uisConfigure}
					</SyntaxHighlighter>
				</Box>
				<Typography component='p'>
					“First name column”, “Last name column”, “Email column”, “tz column” and “Test
					Applicant CF” are all
					column headers that would be found in the CSV that will be uploaded. “7” is the
					internal identifier of
					the custom field that the data from “Test Applicant CF” will go to for the given
					user. You can retrieve
					a list of custom field identifiers using the
					<a href='#?route=get-/api/customfields/'>/api/customfields</a>
					{' '}
					endpoint.
				</Typography>
				<Typography component='p'>
					An imported CSV for this integration may look something
					like this:
				</Typography>
				<Box my={5} ml={5} mr={10} pr={10}>
					<SyntaxHighlighter
						language='csv'
						style={theme}
					>
						{uisSampleImport}
					</SyntaxHighlighter>
				</Box>
				<Typography component='p'>
					Column names can be anything that you want, as long as they are mapped to the
					appropriate field. In the
					configuration we use the format
					{' '}
					<b>user.*</b>
					{' '}
					to identify which Apply field will
					be populated with the
					data that we find under the given column for each user. If, for example, you
					used “GivenName” as the
					column header that contained user first names, the mapping would be as
					below.
					{' '}
					<b>user.first_name</b>
					,
					<b>user.last_name</b>
					,
					<b>user.email</b>
					,
					<b>user.uid</b>
					,
					and
					<b>user.timezone</b>
					{' '}
					are all static names
					that relate to specific fields in Apply.
				</Typography>
				<Box my={5} ml={5} mr={10} pr={10}>
					<SyntaxHighlighter
						language='json'
						style={theme}
					>
						{uisMappingExample}
					</SyntaxHighlighter>
				</Box>
			</Box>
			<Box my={2}>
				<Typography variant='cardTitle' component='h4' weight='medium'>
					Importing
					Users
				</Typography>
				<Typography component='p'>
					When importing users, you
					{' '}
					<b>POST</b>
					{' '}
					to the
					{' '}
					<a
						href='#?route=post-/api/uisimports/'
					>
						/api/uisimports/
					</a>
					endpoint with a valid CSV file. We will validate the file against the UIS
					configuration for your site
					and return a response that will include information about the job that was
					created. For a full list of
					properties to expect, see the documentation for
					<a href='#?route=post-/api/uisimports/'>/api/uisimports/</a>
					.
				</Typography>
			</Box>
			<Box my={2}>
				<Typography variant='cardTitle' component='h4' weight='medium'>
					Unique
					Identifiers
				</Typography>
				<Typography component='p'>
					When an import is processed, by default we attempt to identify existing users
					using the email address
					that is included in the import. In some cases, an alternative way to identify
					the users may be
					preferred, such as by the users student ID, employee number, or some other
					value. These would be unique
					identifiers. Unique identifiers should be universally unique across your
					organization and your Apply
					site, meaning that no two users should have the same ID, and no ID should be
					re-used.
				</Typography>
				<Typography component='p'>
					Unique identifiers are stored as custom field values on Apply, and when the User
					Information System is
					configured to use a unique identifier, it will validate that the CSV includes
					the column that has been
					identified as containing this identifier for users. If it is not included in the
					CSV, an error will
					occur.
				</Typography>
				<Typography component='p'>
					When an import is requested and a unique identifier has been configured, we will
					first try to find a
					user based on matching custom field values. If one is found, we&#39;ll update
					their
					user profile with the
					information passed over in the import. If none are found, we&#39;ll create a new
					user using the details
					provided. You can populate the unique identifiers ahead of time for existing
					users, or when you import
					you can include the custom field that the identifier should go in and populate
					the data using the User
					Information System.
				</Typography>
			</Box>
			<Box my={2}>
				<Box>
					<Typography variant='cardTitle' component='h4' weight='medium'>
						Warnings and
						Errors
					</Typography>
					<Typography component='p'>
						Warnings are issues identified during the import that will not stop the
						import from processing. An
						example of this would be if you identified that you were going to include
						user timezones in your CSV
						but did not include the column that you identified would contain this data.
						If a warning occurs, details
						of the warning will be included in the API response but the import will
						continue unless an error also
						occurs.
					</Typography>
				</Box>
				<Box m={2}>
					<Typography variant='cardTitle' component='h5' weight='medium'>
						Warning
						Example
					</Typography>
					<Box my={5} ml={5} mr={10} pr={10}>
						<SyntaxHighlighter
							language='json'
							style={theme}
						>
							{warningExample}
						</SyntaxHighlighter>
					</Box>
					<Typography component='p'>Here we see</Typography>
					<ul>
						<li>
							The import contains a column that is not mapped to a custom field in the
							configuration. Unmapped
							columns are ignored.
						</li>
						<li>
							We identified that the column “tz” would be included in the import and
							would contain the user’s
							timezone, however the CSV file did not have a column called “tz”. The
							site’s default timezone will
							be used instead when creating users.
						</li>
					</ul>
				</Box>
				<Box m={2}>
					<Typography variant='cardTitle' component='h5' weight='medium'>
						Error
						Example
					</Typography>
					<Box my={5} ml={5} mr={10} pr={10}>
						<SyntaxHighlighter
							language='json'
							style={theme}
						>
							{errorExample}
						</SyntaxHighlighter>
					</Box>
					<Typography component='p'>Here we see</Typography>
					<ul>
						<li>
							The CSV import did not contain the column that we identified would
							contain the user’s first name
							information. In this example, the User Information System configuration
							had “UserFirstNameAttribute”
							as the name of the column that we should look for the user’s first name
							in, but no such column
							header was found in the import CSV.
						</li>
					</ul>
				</Box>
			</Box>
			<Box my={2}>
				<Typography variant='cardTitle' component='h4' weight='medium'>
					The CSV
					File
				</Typography>
				<Typography component='p'>
					This file must contain the user’s first name, last name and email address with
					their respective column
					headers mapped in the UIS configuration. If a unique identifier is configured,
					it must also be sent.
					The column for the unique identifier must match the name of the custom field
					that it will be populated
					in. For example, if you are using the users student id as their unique
					identifier, and you’re sending
					“Student_ID” as the column name in the CSV with this value, you must also have a
					custom field called
					“Student_ID”. For optional fields, you can also include a column for user
					timezones and custom fields.
				</Typography>
			</Box>
			<Typography variant='cardTitle' component='h3' weight='medium'>Examples</Typography>
			<Box my={2}>
				<Typography variant='cardTitle' component='h5' weight='medium'>
					Required attributes only
				</Typography>
				<Box my={5} ml={5} mr={10} pr={10}>
					<SyntaxHighlighter language='csv' style={theme}>
						{requiredAttributesExample}
					</SyntaxHighlighter>
				</Box>
				<Typography variant='cardTitle' component='h5' weight='medium'>
					Required attributes and a unique identifier
				</Typography>
				<Box my={5} ml={5} mr={10} pr={10}>
					<SyntaxHighlighter language='csv' style={theme}>
						{requiredUniqueAttributesExample}
					</SyntaxHighlighter>
				</Box>
				<Typography variant='cardTitle' component='h5' weight='medium'>
					Required Attributes, Unique Identifier, and some Optional columns
				</Typography>
				<Box my={5} ml={5} mr={10} pr={10}>
					<SyntaxHighlighter language='csv' style={theme}>
						{requiredUniqueOptionalAttributesExample}
					</SyntaxHighlighter>
				</Box>
				<Typography variant='cardTitle' component='h5' weight='medium'>
					Job Details
				</Typography>
				<Typography component='p'>
					When a call is made to the api/uisimports/ endpoint with a valid CSV file, a job
					will be created to
					process the import. A response like the one seen below will provide details on
					the job.
				</Typography>
				<Box my={5} ml={5} mr={10} pr={10}>
					<SyntaxHighlighter
						language='json'
						style={theme}
					>
						{sampleJobResponse}
					</SyntaxHighlighter>
				</Box>
				<Typography component='p'>
					You can check the status of the import job by querying
					<a href='#?route=get-/api/uisimports/-id-/'>/api/uisimports/:id/</a>
					.
				</Typography>
			</Box>
		</Box>
	);
}

module.exports = {
	uisConfigure,
	UserInformationSystem,
};
